<template>
  <div class="json-kv-editor" :class="{ 'has-error': parseError || rowError }" :data-json-field="fieldKey">
    <b-table-simple small bordered responsive class="mb-1">
      <b-thead>
        <b-tr>
          <b-th style="width: 40%">{{ keyLabel }}</b-th>
          <b-th style="width: 50%">{{ valueLabel }}</b-th>
          <b-th style="width: 10%"></b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr v-for="(row, idx) in rows" :key="idx + '_' + row.tags.length">
          <b-td>
            <b-form-input
              size="sm"
              v-model="row.key"
              :placeholder="keyPlaceholder"
              @input="emitUpdate"
            />
          </b-td>
          <b-td>
            <template v-if="row.tags.length > 0">
              <div
                v-for="(tag, tidx) in row.tags"
                :key="tidx"
                style="display: flex; gap: 4px; margin-bottom: 3px;"
              >
                <b-form-input
                  size="sm"
                  v-model="row.tags[tidx]"
                  :placeholder="valuePlaceholder"
                  @input="emitUpdate"
                  style="flex: 1;"
                />
                <b-button size="sm" variant="outline-danger" @click="removeTag(idx, tidx)" title="Remove">
                  &times;
                </b-button>
              </div>
              <div style="display: flex; gap: 4px; margin-top: 2px;">
                <b-button size="sm" variant="outline-secondary" @click="addTag(idx)">+ Add</b-button>
              </div>
            </template>
            <template v-else>
              <div style="display: flex; gap: 4px;">
                <b-form-input
                  size="sm"
                  v-model="row.val"
                  :type="valueType === 'number' ? 'number' : 'text'"
                  :placeholder="valuePlaceholder"
                  @input="emitUpdate"
                  style="flex: 1;"
                />
                <b-button size="sm" variant="outline-secondary" @click="convertToList(idx)" title="Switch to list of values">+</b-button>
              </div>
            </template>
          </b-td>
          <b-td class="text-center align-middle">
            <b-button size="sm" variant="outline-danger" @click="removeRow(idx)" title="Remove">
              &times;
            </b-button>
          </b-td>
        </b-tr>
        <b-tr v-if="rows.length === 0">
          <b-td colspan="3" class="text-center text-muted" style="font-style: italic; font-size: 13px;">
            No entries. Click "Add Row" below.
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
    <div class="d-flex align-items-center" style="gap: 8px;">
      <b-button size="sm" variant="outline-primary" @click="addRow">+ Add Row</b-button>
      <b-button v-if="allowRawJson" size="sm" variant="outline-secondary" @click="showRaw = !showRaw">
        {{ showRaw ? 'Hide' : 'Show' }} Raw JSON
      </b-button>
    </div>
    <b-form-textarea
      v-if="allowRawJson && showRaw"
      :value="value"
      @input="onRawInput"
      rows="3"
      class="mt-2"
      style="font-family: monospace; font-size: 13px;"
    />
  </div>
</template>

<script>
export default {
  name: 'JsonKeyValueEditor',
  props: {
    value: { type: String, default: '' },
    fieldKey: { type: String, default: '' },
    keyLabel: { type: String, default: 'Key' },
    valueLabel: { type: String, default: 'Value' },
    keyPlaceholder: { type: String, default: '' },
    valuePlaceholder: { type: String, default: '' },
    valueType: { type: String, default: 'string' },
    allowRawJson: { type: Boolean, default: false },
  },
  created() {
    this._parseTimer = null
  },
  beforeDestroy() {
    if (this._parseTimer) clearTimeout(this._parseTimer)
  },
  data() {
    return {
      rows: [],
      showRaw: false,
      parseError: '',
      rowError: '',
      internalUpdate: false,
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (this.internalUpdate) {
          this.internalUpdate = false
          return
        }
        this.parseValue(newVal)
      },
    },
    parseError(val) {
      if (val && this.allowRawJson) this.showRaw = true
    },
    allowRawJson(val) {
      if (!val) {
        this.showRaw = false
        if (this._parseTimer) {
          clearTimeout(this._parseTimer)
          this._parseTimer = null
        }
      }
    },
  },
  methods: {
    parseValue(jsonStr) {
      this.parseError = ''
      this.rowError = ''
      if (!jsonStr || (typeof jsonStr === 'string' && jsonStr.trim() === '')) {
        this.rows = []
        this.emitValidation('')
        return
      }
      try {
        const obj = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr
        if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
          this.rows = Object.entries(obj).map(function(entry) {
            var key = entry[0]
            var val = entry[1]
            return {
              key: key,
              val: Array.isArray(val) ? '' : String(val),
              tags: Array.isArray(val) ? val.map(String) : [],
            }
          })
          this.emitValidation('')
        } else {
          this.parseError = 'Expected a JSON object (not array or primitive)'
          this.rows = []
          this.emitValidation(this.parseError)
        }
      } catch (e) {
        this.parseError = 'Invalid JSON: ' + e.message
        this.rows = []
        this.emitValidation(this.parseError)
      }
    },
    buildObject(includeIncomplete) {
      var obj = {}
      for (var i = 0; i < this.rows.length; i++) {
        var row = this.rows[i]
        var k = (row.key || '').trim()
        var hasValue = row.tags.length > 0
          ? row.tags.some(function(t) { return t.trim() !== '' })
          : (row.val || '').trim() !== ''
        if (!k && !hasValue) continue
        if (!k && !includeIncomplete) continue
        if (row.tags.length > 0) {
          obj[k] = row.tags.filter(function(t) { return t.trim() !== '' })
        } else if (this.valueType === 'number') {
          var num = Number(row.val)
          obj[k] = isNaN(num) ? row.val : num
        } else {
          obj[k] = row.val
        }
      }
      return obj
    },
    validateRows() {
      for (var i = 0; i < this.rows.length; i++) {
        var row = this.rows[i]
        var k = (row.key || '').trim()
        var hasValue = row.tags.length > 0
          ? row.tags.some(function(t) { return t.trim() !== '' })
          : (row.val || '').trim() !== ''
        if (!k && hasValue) return 'All rows must have a key'
      }
      return ''
    },
    emitUpdate() {
      var rowErr = this.validateRows()
      this.rowError = rowErr
      this.emitValidation(rowErr || this.parseError)
      this.internalUpdate = true
      // When there's a row error include incomplete rows so the parent sees a
      // change and triggers dirty state — save stays blocked by the validation event
      var obj = this.buildObject(!!rowErr)
      var json = Object.keys(obj).length > 0 ? JSON.stringify(obj) : ''
      this.$emit('input', json)
      // If the emitted value equals the current prop Vue won't re-set it,
      // so the watcher won't fire and internalUpdate would stay true forever.
      this.$nextTick(function() { this.internalUpdate = false }.bind(this))
    },
    onRawInput(val) {
      this.internalUpdate = true
      this.$emit('input', val)
      if (this._parseTimer) clearTimeout(this._parseTimer)
      this._parseTimer = setTimeout(function() {
        this.parseValue(val)
      }.bind(this), 1000)
    },
    addRow() {
      this.rows.push({ key: '', val: '', tags: [] })
      this.emitUpdate()
    },
    removeRow(idx) {
      this.rows.splice(idx, 1)
      this.emitUpdate()
    },
    replaceRow(rowIdx, newRow) {
      this.rows = this.rows.map(function(row, i) { return i === rowIdx ? newRow : row })
    },
    addTag(rowIdx) {
      var row = this.rows[rowIdx]
      this.replaceRow(rowIdx, { key: row.key, val: row.val, tags: row.tags.concat(['']) })
    },
    removeTag(rowIdx, tagIdx) {
      var row = this.rows[rowIdx]
      var newTags = row.tags.filter(function(_, i) { return i !== tagIdx })
      if (newTags.length <= 1) {
        // Revert to single-value mode — preserves the remaining value as a
        // plain string so the emitted JSON matches what was there before
        // convertToList was called, keeping the dirty state clean.
        this.replaceRow(rowIdx, { key: row.key, val: newTags[0] || '', tags: [] })
      } else {
        this.replaceRow(rowIdx, { key: row.key, val: row.val, tags: newTags })
      }
      this.emitUpdate()
    },
    convertToList(rowIdx) {
      var row = this.rows[rowIdx]
      this.replaceRow(rowIdx, { key: row.key, val: '', tags: [row.val || '', ''] })
      this.emitUpdate()
    },
    emitValidation(error) {
      if (this.fieldKey) {
        this.$emit('validation', this.fieldKey, error)
      }
    },
  },
}
</script>

<style scoped>
.json-kv-editor .table {
  margin-bottom: 4px;
}
.json-kv-editor .table td {
  padding: 4px 6px;
  vertical-align: middle;
}
.json-kv-editor .table th {
  padding: 4px 6px;
  font-size: 13px;
  font-weight: 600;
  background: #f8f9fa;
}
.json-kv-editor.has-error .table {
  border-color: #dc3545;
}
</style>

